import type { Client } from "@/api/schemas";
import { rutaDeCliente } from "./routeConfig";

/**
 * Rutas del panel anterior (interaction.md §7). Son permanentes: los avisos de Telegram y los correos ya
 * llevan estos enlaces. Cada una redirige con `replace` al mismo contenido bajo `/c/{cliente}/`, y los
 * parámetros que cambiaron de nombre se traducen SOLO en el destino cuya pantalla ya lee el nombre nuevo.
 */
export interface RedireccionAntigua {
  /** Patrón de la ruta antigua (con `:parámetros` de react-router). */
  desde: string;
  /** Sufijo bajo `/c/{cliente}/`, con los mismos `:parámetros`. */
  hacia: string;
  renombres?: Readonly<Record<string, string>>;
}

const RESULTADOS = { window: "periodo", platform: "plataforma" } as const;
const HISTORIAL = { event_type: "tipo" } as const;

export const REDIRECCIONES_ANTIGUAS: readonly RedireccionAntigua[] = [
  { desde: "/resultados", hacia: "resultados", renombres: RESULTADOS },
  { desde: "/cartera", hacia: "resultados", renombres: RESULTADOS },
  { desde: "/cockpit", hacia: "resultados", renombres: RESULTADOS },
  { desde: "/senales", hacia: "resultados", renombres: RESULTADOS },
  { desde: "/campanas", hacia: "campanas" },
  { desde: "/creatividades", hacia: "campanas" },
  { desde: "/runtime/vincular", hacia: "campanas" },
  { desde: "/campanas-fisicas", hacia: "campanas/fisicas" },
  { desde: "/leads", hacia: "leads" },
  { desde: "/whatsapp", hacia: "whatsapp" },
  { desde: "/propuestas", hacia: "propuestas", renombres: { proposal_id: "propuesta" } },
  { desde: "/propuestas/historial", hacia: "propuestas/historial", renombres: HISTORIAL },
  { desde: "/propuestas/lanzamiento/:slug", hacia: "propuestas/lanzamiento/:slug" },
  { desde: "/propuestas/paquete", hacia: "propuestas/paquete" },
  { desde: "/propuestas/paquete/:paqueteId", hacia: "propuestas/paquete/:paqueteId" },
  { desde: "/registro", hacia: "propuestas/historial", renombres: HISTORIAL },
  { desde: "/ajustes", hacia: "ajustes" },
  { desde: "/ajustes/negocio", hacia: "ajustes/negocio" },
  { desde: "/ajustes/avanzado", hacia: "ajustes/avanzado" },
  { desde: "/reglas", hacia: "ajustes/avanzado" },
  { desde: "/ajustes/landings", hacia: "landings" },
  { desde: "/ajustes/landings/:slug", hacia: "landings/:slug" },
  { desde: "/conexiones", hacia: "conexiones", renombres: { provider: "proveedor" } },
];

interface EntradaDestino {
  params: Readonly<Record<string, string | undefined>>;
  search: string;
  cliente: Client;
}

/** `business_id` solo servía para elegir cliente (lo resuelve quien llama); no viaja al destino. */
export function destinoDeRedireccion(redireccion: RedireccionAntigua, { params, search, cliente }: EntradaDestino): string {
  const sufijo = redireccion.hacia.replace(/:(\w+)/g, (_, nombre: string) => encodeURIComponent(params[nombre] ?? ""));
  const origen = new URLSearchParams(search);
  const destino = new URLSearchParams();
  for (const [clave, valor] of origen) {
    if (clave === "business_id") continue;
    destino.append(redireccion.renombres?.[clave] ?? clave, valor);
  }
  const query = destino.toString();
  return `${rutaDeCliente(cliente.slug, sufijo)}${query ? `?${query}` : ""}`;
}

/** `/login` sigue vivo (el servidor redirige ahí los fallos de Google): va a `/entrar` con `next` → `volver`. */
export function redireccionDeLogin(search: string): string {
  const origen = new URLSearchParams(search);
  const destino = new URLSearchParams();
  for (const [clave, valor] of origen) destino.append(clave === "next" ? "volver" : clave, valor);
  const query = destino.toString();
  return `/entrar${query ? `?${query}` : ""}`;
}
